import { copyText } from "@/lib/uid.ts";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { FEATURE_TEMPLATES, FEATURES, type Feature } from "@codvero/shared";
import { Check, Copy, KeyRound, ShieldCheck, UserPlus, X } from "lucide-react";
import { cn } from "@/lib/cn.ts";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { usersApi, type ActivityEntry, type AdminUser } from "@/api/users.ts";
import { googleApi } from "@/api/imports.ts";
import { Alert, Button, Card, Field, Input, Select } from "@/ui/primitives.tsx";
import { useApiError } from "@/ui/useApiError.ts";
import { useConfirm } from "@/ui/ConfirmDialog.tsx";

export function UsersSettings({ selfId }: { selfId: string }) {
  return (
    <div className="space-y-6">
      <InvitesCard />
      <GoogleSignInCard />
      <UsersCard selfId={selfId} />
      <ActivityCard />
    </div>
  );
}

function GoogleSignInCard() {
  const { t } = useTranslation();
  const toMsg = useApiError();
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ["settings", "google-signin"], queryFn: googleApi.signInSettings });
  const [domains, setDomains] = useState<string | null>(null);
  const save = useMutation({
    mutationFn: (v: { enabled?: boolean; autoRegister?: boolean; allowedDomains?: string[] }) => googleApi.setSignInSettings({ enabled: q.data?.enabled ?? false, autoRegister: q.data?.autoRegister ?? false, allowedDomains: q.data?.allowedDomains ?? [], ...v }),
    onSuccess: (d) => { qc.setQueryData(["settings", "google-signin"], d); void qc.invalidateQueries({ queryKey: ["auth", "status"] }); setDomains(null); },
  });
  const d = q.data;
  return (
    <Card className="space-y-3">
      <h2 className="font-semibold">{t("settings.users.google")}</h2>
      <p className="text-sm text-fg-muted">{t("settings.users.googleHint")}</p>
      {d && !d.configured && <Alert tone="warn">{t("settings.users.googleNotConfigured")}</Alert>}
      {save.error && <Alert tone="danger">{toMsg(save.error)}</Alert>}
      {d && (
        <div className="space-y-2 text-sm">
          <label className="flex items-center gap-2"><input type="checkbox" className="accent-accent" disabled={!d.configured} checked={d.enabled} onChange={(e) => save.mutate({ enabled: e.target.checked })} /> {t("settings.users.googleEnable")}</label>
          <label className="flex items-center gap-2"><input type="checkbox" className="accent-accent" disabled={!d.enabled} checked={d.autoRegister} onChange={(e) => save.mutate({ autoRegister: e.target.checked })} /> {t("settings.users.googleAuto")}</label>
          <Field label={t("settings.users.googleDomains")} hint={t("settings.users.googleDomainsHint")}>
            <div className="flex gap-2">
              <Input dir="ltr" disabled={!d.enabled} placeholder="corp.example, team.example" value={domains ?? d.allowedDomains.join(", ")} onChange={(e) => setDomains(e.target.value)} />
              <Button type="button" variant="secondary" disabled={domains === null} loading={save.isPending} onClick={() => save.mutate({ allowedDomains: (domains ?? "").split(/[,\s]+/).map((x) => x.trim().toLowerCase()).filter(Boolean) })}>{t("common.save")}</Button>
            </div>
          </Field>
        </div>
      )}
    </Card>
  );
}

function InvitesCard() {
  const { t, i18n } = useTranslation();
  const toMsg = useApiError();
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ["users", "invites"], queryFn: usersApi.invites });
  const [form, setForm] = useState({ email: "", role: "member" as "member" | "admin", expiresInHours: 72 });
  const [fresh, setFresh] = useState<{ code: string } | null>(null);
  const [copied, setCopied] = useState(false);
  const create = useMutation({
    mutationFn: () => usersApi.createInvite({ role: form.role, expiresInHours: form.expiresInHours, ...(form.email ? { email: form.email } : {}) }),
    onSuccess: (inv) => { setFresh({ code: inv.code }); setCopied(false); void qc.invalidateQueries({ queryKey: ["users", "invites"] }); },
  });
  const remove = useMutation({ mutationFn: usersApi.deleteInvite, onSuccess: () => qc.invalidateQueries({ queryKey: ["users", "invites"] }) });
  const { confirm } = useConfirm();
  const askRemove = async (id: string) => { if (await confirm({ title: t("confirm.deleteInviteTitle"), message: t("confirm.deleteInviteMsg"), confirmLabel: t("common.delete"), danger: true })) remove.mutate(id); };
  const fmt = (iso: string) => new Intl.DateTimeFormat(i18n.language, { dateStyle: "medium", timeStyle: "short" }).format(new Date(iso));
  const status = (inv: { usedAt: string | null; expiresAt: string }) =>
    inv.usedAt ? t("settings.users.used") : new Date(inv.expiresAt) < new Date() ? t("settings.users.expired") : t("settings.users.active");
  const registerUrl = fresh ? `${location.origin}/register?invite=${encodeURIComponent(fresh.code)}` : "";

  return (
    <Card className="space-y-4">
      <h2 className="font-semibold">{t("settings.users.invites")}</h2>
      <form className="grid items-end gap-3 sm:grid-cols-[1fr_auto_auto_auto]" onSubmit={(e) => { e.preventDefault(); create.mutate(); }}>
        <Field label={t("settings.users.inviteEmail")}>
          <Input type="email" dir="ltr" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
        </Field>
        <Field label={t("settings.users.inviteRole")}>
          <Select value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value as "member" | "admin" })}>
            <option value="member">{t("common.role.member")}</option>
            <option value="admin">{t("common.role.admin")}</option>
          </Select>
        </Field>
        <Field label={t("settings.users.inviteExpires")}>
          <Select value={form.expiresInHours} onChange={(e) => setForm({ ...form, expiresInHours: Number(e.target.value) })}>
            {[24, 72, 168].map((h) => <option key={h} value={h}>{t("settings.users.hours", { count: h })}</option>)}
          </Select>
        </Field>
        <Button type="submit" loading={create.isPending}>{t("settings.users.create")}</Button>
      </form>
      {create.error && <Alert tone="danger">{toMsg(create.error)}</Alert>}
      {fresh && (
        <Alert tone="success" className="space-y-2">
          <div>{t("settings.users.codeOnce")}</div>
          <div className="flex items-center gap-2">
            <code className="rounded bg-surface px-2 py-1 font-mono text-base tracking-wider">{fresh.code}</code>
            <Button
              size="sm"
              variant="secondary"
              onClick={async () => { await copyText(registerUrl); setCopied(true); }}
            >
              {copied ? <Check className="size-3.5" /> : <Copy className="size-3.5" />} {copied ? t("common.copied") : t("common.copy")}
            </Button>
          </div>
          <div dir="ltr" className="truncate text-xs text-fg-muted text-start">{registerUrl}</div>
        </Alert>
      )}
      <ul className="divide-y divide-border text-sm">
        {(q.data ?? []).map((inv) => (
          <li key={inv.id} className="flex items-center justify-between gap-3 py-2">
            <div className="min-w-0">
              <span className="font-mono">{inv.code}</span>
              <span className="ms-2 text-fg-muted">{t(`common.role.${inv.role}`)}</span>
              {inv.email && <span dir="ltr" className="ms-2 text-fg-muted">{inv.email}</span>}
              <div className="text-xs text-fg-muted">{status(inv)} · {fmt(inv.expiresAt)}</div>
            </div>
            {!inv.usedAt && <Button variant="ghost" size="sm" onClick={() => void askRemove(inv.id)}>{t("common.delete")}</Button>}
          </li>
        ))}
      </ul>
    </Card>
  );
}

function UsersCard({ selfId }: { selfId: string }) {
  const { t, i18n } = useTranslation();
  const toMsg = useApiError();
  const qc = useQueryClient();
  const { confirm } = useConfirm();
  const q = useQuery({ queryKey: ["users"], queryFn: usersApi.list });
  const refresh = () => void qc.invalidateQueries({ queryKey: ["users"] });
  const toggle = useMutation({ mutationFn: ({ id, disabled }: { id: string; disabled: boolean }) => (disabled ? usersApi.enable(id) : usersApi.disable(id)), onSuccess: refresh });
  const resetTotp = useMutation({ mutationFn: (id: string) => usersApi.resetTotp(id), onSuccess: refresh });
  const [dialog, setDialog] = useState<{ kind: "new" } | { kind: "features"; user: AdminUser } | { kind: "password"; user: AdminUser } | null>(null);
  const fmt = (iso: string) => new Intl.DateTimeFormat(i18n.language, { dateStyle: "medium" }).format(new Date(iso));
  const err = toggle.error ?? resetTotp.error;
  return (
    <Card className="space-y-3">
      <div className="flex items-center gap-2">
        <h2 className="flex-1 font-semibold">{t("settings.users.users")}</h2>
        <Button size="sm" onClick={() => setDialog({ kind: "new" })}><UserPlus className="size-4" /> {t("settings.users.newUser")}</Button>
      </div>
      {err && <Alert tone="danger">{toMsg(err)}</Alert>}
      <ul className="divide-y divide-border text-sm">
        {(q.data ?? []).map((u) => {
          const self = u.id === selfId;
          return (
            <li key={u.id} className="space-y-1.5 py-3">
              <div className="flex flex-wrap items-center gap-2">
                <span className="font-medium">{u.name}</span>
                {u.owner ? <span className="rounded bg-accent-soft px-1.5 py-0.5 text-xs text-accent">{t("settings.users.owner")}</span> : u.features.includes("admin") && <span className="rounded bg-surface-2 px-1.5 py-0.5 text-xs text-fg-muted">{t("settings.users.admin")}</span>}
                {u.disabledAt && <span className="rounded bg-danger-soft px-1.5 py-0.5 text-xs text-danger">{t("settings.users.disabled")}</span>}
                {u.totpEnabled && <span className="rounded bg-success-soft px-1.5 py-0.5 text-xs text-success">{t("settings.users.totpOn")}</span>}
                {u.mustChangePassword && <span className="rounded bg-warn-soft px-1.5 py-0.5 text-xs">{t("settings.users.mustChange")}</span>}
              </div>
              <div className="text-xs text-fg-muted"><span dir="ltr">{u.email}</span> · {t("settings.users.joined")} {fmt(u.createdAt)}</div>
              {!u.owner && (
                <div className="flex flex-wrap gap-1">
                  {u.features.length === 0 && <span className="text-xs text-fg-muted">{t("settings.users.noFeatures")}</span>}
                  {u.features.filter((f) => f !== "admin").map((f) => <span key={f} className="rounded-full border border-border px-2 py-0.5 text-[11px] text-fg-muted">{t(`settings.users.feature.${f}`)}</span>)}
                </div>
              )}
              {!self && !u.owner && (
                <div className="flex flex-wrap gap-1.5 pt-1">
                  <Button size="sm" variant="secondary" onClick={() => setDialog({ kind: "features", user: u })}><ShieldCheck className="size-3.5" /> {t("settings.users.permissions")}</Button>
                  <Button size="sm" variant="secondary" onClick={() => setDialog({ kind: "password", user: u })}><KeyRound className="size-3.5" /> {t("settings.users.resetPassword")}</Button>
                  {u.totpEnabled && <Button size="sm" variant="secondary" loading={resetTotp.isPending && resetTotp.variables === u.id} onClick={async () => { if (await confirm({ title: t("settings.users.resetTotp"), message: t("settings.users.resetTotpMsg", { name: u.name }), confirmLabel: t("settings.users.resetTotp"), danger: true })) resetTotp.mutate(u.id); }}>{t("settings.users.resetTotp")}</Button>}
                  <Button variant={u.disabledAt ? "secondary" : "danger"} size="sm" loading={toggle.isPending && toggle.variables?.id === u.id} onClick={() => toggle.mutate({ id: u.id, disabled: !!u.disabledAt })}>
                    {u.disabledAt ? t("settings.users.enable") : t("settings.users.disable")}
                  </Button>
                </div>
              )}
            </li>
          );
        })}
      </ul>
      {dialog?.kind === "new" && <UserDialog onClose={() => setDialog(null)} onDone={refresh} />}
      {dialog?.kind === "features" && <UserDialog user={dialog.user} onClose={() => setDialog(null)} onDone={refresh} />}
      {dialog?.kind === "password" && <PasswordDialog user={dialog.user} onClose={() => setDialog(null)} onDone={refresh} />}
    </Card>
  );
}

/** A password strong enough for the platform's rule, easy to read out (no 0/O, 1/l). */
function newPassword(): string {
  const abc = "ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnpqrstuvwxyz23456789";
  const bytes = crypto.getRandomValues(new Uint8Array(16));
  return Array.from(bytes, (b) => abc[b % abc.length]).join("").replace(/(.{4})(?!$)/g, "$1-");
}

function Modal({ title, onClose, children, footer }: { title: string; onClose: () => void; children: React.ReactNode; footer: React.ReactNode }) {
  return (
    <div className="fixed inset-0 z-50 grid place-items-center bg-black/50 p-4" onClick={onClose} onKeyDown={(e) => { if (e.key === "Escape") onClose(); }}>
      <div role="dialog" aria-modal="true" aria-label={title} className="flex max-h-[90vh] w-full max-w-lg flex-col rounded-lg border border-border bg-surface shadow-xl" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between border-b border-border p-4"><h2 className="font-semibold">{title}</h2><button type="button" aria-label="close" onClick={onClose} className="rounded p-1 hover:bg-surface-2"><X className="size-5" /></button></div>
        <div className="min-h-0 flex-1 space-y-3 overflow-y-auto p-4">{children}</div>
        <div className="flex justify-end gap-2 border-t border-border p-4">{footer}</div>
      </div>
    </div>
  );
}

/** Feature checkboxes with the ready-made starting points above them. */
function FeaturePicker({ value, onChange }: { value: Feature[]; onChange: (v: Feature[]) => void }) {
  const { t } = useTranslation();
  return (
    <div className="space-y-2">
      <p className="text-xs text-fg-muted">{t("settings.users.templates")}</p>
      <div className="flex flex-wrap gap-1.5">
        {(Object.keys(FEATURE_TEMPLATES) as (keyof typeof FEATURE_TEMPLATES)[]).map((k) => {
          const on = FEATURE_TEMPLATES[k].length === value.length && FEATURE_TEMPLATES[k].every((f) => value.includes(f));
          return <button key={k} type="button" onClick={() => onChange([...FEATURE_TEMPLATES[k]])} className={cn("rounded-full border px-2.5 py-1 text-xs", on ? "border-accent bg-accent-soft" : "border-border text-fg-muted hover:text-fg")}>{t(`settings.users.template.${k}`)}</button>;
        })}
      </div>
      <p className="pt-1 text-xs text-fg-muted">{t("settings.users.features")}</p>
      <ul className="space-y-1">
        {FEATURES.map((f) => (
          <li key={f}>
            <label className={cn("flex cursor-pointer items-start gap-2 rounded-md border px-3 py-2", value.includes(f) ? "border-accent/60 bg-accent-soft/40" : "border-border")}>
              <input type="checkbox" className="mt-0.5 accent-accent" checked={value.includes(f)} onChange={(e) => onChange(e.target.checked ? FEATURES.filter((x) => x === f || value.includes(x)) : value.filter((x) => x !== f))} />
              <span className="min-w-0">
                <span className="block text-sm font-medium">{t(`settings.users.feature.${f}`)}</span>
                <span className="block text-xs text-fg-muted">{t(`settings.users.featureHint.${f}`)}</span>
              </span>
            </label>
          </li>
        ))}
      </ul>
    </div>
  );
}

function CopyField({ value, onChange, onGenerate }: { value: string; onChange: (v: string) => void; onGenerate: () => void }) {
  const { t } = useTranslation();
  const [copied, setCopied] = useState(false);
  return (
    <div className="flex gap-2">
      <Input dir="ltr" required minLength={12} className="font-mono" value={value} onChange={(e) => onChange(e.target.value)} />
      <Button type="button" variant="secondary" onClick={onGenerate}>{t("settings.users.generate")}</Button>
      <Button type="button" variant="ghost" aria-label={t("settings.users.copied")} onClick={() => { void copyText(value); setCopied(true); setTimeout(() => setCopied(false), 1500); }}>{copied ? <Check className="size-4" /> : <Copy className="size-4" />}</Button>
    </div>
  );
}

/** A new account (name, email, a password to hand over, features) or an existing one's features. */
function UserDialog({ user, onClose, onDone }: { user?: AdminUser; onClose: () => void; onDone: () => void }) {
  const { t } = useTranslation();
  const toMsg = useApiError();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState(newPassword);
  const [features, setFeatures] = useState<Feature[]>(user ? user.features : [...FEATURE_TEMPLATES.allButSettings]);
  const save = useMutation({
    mutationFn: () => (user ? usersApi.setFeatures(user.id, features) : usersApi.create({ name: name.trim(), email: email.trim(), password, features })),
    onSuccess: () => { onDone(); onClose(); },
  });
  return (
    <Modal
      title={user ? t("settings.users.editFeatures", { name: user.name }) : t("settings.users.newUser")}
      onClose={onClose}
      footer={<><Button type="button" variant="ghost" onClick={onClose}>{t("common.cancel")}</Button><Button type="button" loading={save.isPending} disabled={!user && (!name.trim() || !email.trim() || password.length < 12)} onClick={() => save.mutate()}>{t("common.save")}</Button></>}
    >
      {!user && <p className="text-xs text-fg-muted">{t("settings.users.newUserHint")}</p>}
      {!user && (
        <>
          <Field label={t("settings.users.name")}><Input autoFocus dir="auto" maxLength={80} value={name} onChange={(e) => setName(e.target.value)} /></Field>
          <Field label={t("settings.users.email")}><Input dir="ltr" type="email" value={email} onChange={(e) => setEmail(e.target.value)} /></Field>
          <Field label={t("settings.users.password")}><CopyField value={password} onChange={setPassword} onGenerate={() => setPassword(newPassword())} /></Field>
        </>
      )}
      <FeaturePicker value={features} onChange={setFeatures} />
      {user && <p className="text-xs text-fg-muted">{t("settings.users.signedOutNote")}</p>}
      {save.error && <Alert tone="danger">{toMsg(save.error)}</Alert>}
    </Modal>
  );
}

function PasswordDialog({ user, onClose, onDone }: { user: AdminUser; onClose: () => void; onDone: () => void }) {
  const { t } = useTranslation();
  const toMsg = useApiError();
  const [password, setPassword] = useState(newPassword);
  const save = useMutation({ mutationFn: () => usersApi.resetPassword(user.id, password), onSuccess: () => { onDone(); onClose(); } });
  return (
    <Modal title={t("settings.users.resetPassword")} onClose={onClose} footer={<><Button type="button" variant="ghost" onClick={onClose}>{t("common.cancel")}</Button><Button type="button" loading={save.isPending} disabled={password.length < 12} onClick={() => save.mutate()}>{t("common.save")}</Button></>}>
      <p className="text-xs text-fg-muted">{t("settings.users.resetPasswordHint", { name: user.name })}</p>
      <CopyField value={password} onChange={setPassword} onGenerate={() => setPassword(newPassword())} />
      {save.error && <Alert tone="danger">{toMsg(save.error)}</Alert>}
    </Modal>
  );
}

const KINDS = ["auth", "user", "invite", "project", "account", "deployment"] as const;

/** What happened on the platform, newest first; filtered by person and kind, 100 at a time. */
function ActivityCard() {
  const { t, i18n } = useTranslation();
  const users = useQuery({ queryKey: ["users"], queryFn: usersApi.list });
  const [actor, setActor] = useState("");
  const [kind, setKind] = useState("");
  const [pages, setPages] = useState<ActivityEntry[][]>([]);
  const first = useQuery({ queryKey: ["users", "activity", actor, kind], queryFn: () => usersApi.activity({ actor, kind }) });
  useEffect(() => setPages([]), [actor, kind]);
  const all = [...(first.data ?? []), ...pages.flat()];
  const more = useMutation({ mutationFn: () => usersApi.activity({ actor, kind, before: all.at(-1)!.id }), onSuccess: (p) => setPages((x) => [...x, p]) });
  const fmt = (iso: string) => new Intl.DateTimeFormat(i18n.language, { dateStyle: "short", timeStyle: "short" }).format(new Date(iso));
  const label = (a: string) => t(`settings.users.activity.actions.${a.replace(/\./g, "_")}`, { defaultValue: a });
  // another page exists while the last one came back full
  const hasMore = ((pages.at(-1) ?? first.data)?.length ?? 0) === 100;
  return (
    <Card className="space-y-3">
      <h2 className="font-semibold">{t("settings.users.activity.title")}</h2>
      <div className="flex flex-wrap gap-2">
        <Select value={actor} onChange={(e) => setActor(e.target.value)} className="h-8 w-auto text-xs">
          <option value="">{t("settings.users.activity.everyone")}</option>
          {(users.data ?? []).map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}
        </Select>
        <Select value={kind} onChange={(e) => setKind(e.target.value)} className="h-8 w-auto text-xs">
          <option value="">{t("settings.users.activity.allKinds")}</option>
          {KINDS.map((k) => <option key={k} value={k}>{t(`settings.users.activity.kinds.${k}`)}</option>)}
        </Select>
      </div>
      <ul className="divide-y divide-border text-xs">
        {all.map((e) => (
          <li key={e.id} className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5 py-1.5">
            <span className="shrink-0 text-fg-muted" dir="ltr">{fmt(e.at)}</span>
            <span className={cn("font-medium", /failed|disabled|deleted/.test(e.action) && "text-danger")}>{label(e.action)}</span>
            {e.actor && <span className="text-fg-muted">· {e.actor}</span>}
            {e.targetName && e.target !== e.actorId && <span>→ {e.targetName}</span>}
            {e.ip && <span dir="ltr" className="ms-auto text-[10px] text-fg-muted">{e.ip}</span>}
          </li>
        ))}
      </ul>
      {first.data?.length === 0 && <p className="text-center text-xs text-fg-muted">{t("settings.users.activity.empty")}</p>}
      {hasMore && <Button size="sm" variant="secondary" loading={more.isPending} onClick={() => more.mutate()}>{t("settings.users.activity.more")}</Button>}
    </Card>
  );
}
